const BASE_WS = process.env.REACT_APP_AI_WS_URL || ('wss://' + (typeof window !== 'undefined' && window.location ? window.location.host : 'dev.callessential.cc') + '/api/ai');

type MessageHandler = (payload: Record<string, unknown>) => void;

export class AIWebSocketService {
  private ws: WebSocket | null = null;
  private handlers: Map<string, MessageHandler[]> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 2000;
  private url = '';

  connect(url: string) {
    this.url = url;
    this._open();
  }

  private _open() {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.reconnectDelay = 2000;
      this._emit('connected', {});
    };

    this.ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type === 'ping') {
          this.send({ type: 'pong' });
          return;
        }
        this._emit(data.type, data);
        this._emit('*', data);
      } catch { /* ignore malformed */ }
    };

    this.ws.onclose = () => {
      this._emit('disconnected', {});
      this._scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private _scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._open();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000);
  }

  send(payload: Record<string, unknown>) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  on(event: string, handler: MessageHandler) {
    if (!this.handlers.has(event)) this.handlers.set(event, []);
    this.handlers.get(event)!.push(handler);
  }

  off(event: string, handler: MessageHandler) {
    const list = this.handlers.get(event) || [];
    this.handlers.set(event, list.filter(h => h !== handler));
  }

  private _emit(event: string, payload: Record<string, unknown>) {
    (this.handlers.get(event) || []).forEach(h => h(payload));
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }
}

export const aiWS = new AIWebSocketService();

export function connectConversationWS(
  convId: string,
  token: string | null,
  userType: 'customer' | 'agent' | 'supervisor',
  customerId: string
): AIWebSocketService {
  const svc = new AIWebSocketService();
  const params = new URLSearchParams({ user_type: userType, customer_id: customerId });
  if (token) params.append('token', token);
  svc.connect(`${BASE_WS}/ws/conversation/${convId}?${params}`);
  return svc;
}

export function connectAgentQueueWS(token: string): AIWebSocketService {
  const svc = new AIWebSocketService();
  svc.connect(`${BASE_WS}/ws/agent-queue?token=${token}`);
  return svc;
}
